import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { getProfile } from '../apis/getProfile';
import { generateIdeasCombined } from '../apis/generateIdeasCombined';
import { generatePost } from '../apis/generatePost';
import { generatePostsCombined } from '../apis/generatePostsCombined';
import { generatePostsGuest } from '../apis/generatePostsGuest';
import { getCampaign, publishCampaign as publishCampaignApi } from '../apis/campaign';
import {
    toIdeasApiPayload,
    toGeneratePostsCombinedPayload,
    toGeneratePostsGuestPayload,
    toGeneratePostPayload,
    normalizeGeneratePostResponse,
    toPublishCampaignPayload,
    normalizePublishCampaignResponse,
    normalizeCampaignFromApi,
} from '../utils/campaignPayload';
import { getQuickCampaignDefaults, formatQuickCampaignName } from '../utils/quickCampaignDefaults';
import { getCampaignBuilderDefaults } from '../utils/campaignBuilderDefaults';
import { buildOpportunityCampaignNameFromList } from '../utils/opportunityCampaignName';
import { getFollowUpOpportunities } from '../utils/opportunitySelection';
import { WIZARD_HASHTAGS_ENABLED } from '../constants/campaignWizard';
import { fetchCurrentPlan } from './planSlice';

export const initiateCampaign = createAsyncThunk(
    'campaignBuilder/initiateCampaign',
    async (_, { rejectWithValue }) => {
        try {
            const data = await getProfile();
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const loadCampaign = createAsyncThunk(
    'campaignBuilder/loadCampaign',
    async (campaignId, { rejectWithValue }) => {
        try {
            const data = await getCampaign(campaignId);
            return normalizeCampaignFromApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const startQuickCampaign = createAsyncThunk(
    'campaignBuilder/startQuickCampaign',
    async ({ tone, topic, target, hashtags = [] }, { getState, rejectWithValue }) => {
        try {
            const userId = getState().auth.user?.uid;
            const defaults = getCampaignBuilderDefaults(userId);
            const basicDetails = getQuickCampaignDefaults(userId);
            const followUp = {
                tone,
                topic,
                target: target.trim() || defaults.followUp.target,
                hashtags,
                opportunities: [],
            };
            const payload = toIdeasApiPayload({ basicDetails, followUp });
            const data = await generateIdeasCombined(payload);

            return {
                campaignId: data.campaign_id ?? data.campaign?.id ?? null,
                basicDetails,
                followUp,
                ideas: data.ideas ?? [],
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchIdeasCombined = createAsyncThunk(
    'campaignBuilder/fetchIdeasCombined',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { basicDetails, followUp } = getState().campaignBuilder;
            const payload = toIdeasApiPayload({ basicDetails, followUp });
            const data = await generateIdeasCombined(payload);
            return {
                campaignId: data.campaign_id ?? data.campaign?.id ?? null,
                ideas: data.ideas ?? [],
                campaign: data.campaign ?? null,
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchPostsCombined = createAsyncThunk(
    'campaignBuilder/fetchPostsCombined',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { campaignId, basicDetails, followUp } = getState().campaignBuilder;
            const payload = toGeneratePostsCombinedPayload({ campaignId, basicDetails, followUp });
            const data = await generatePostsCombined(payload);
            return normalizeGeneratePostResponse(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchPostsGuest = createAsyncThunk(
    'campaignBuilder/fetchPostsGuest',
    async ({ guestId } = {}, { getState, rejectWithValue }) => {
        try {
            const { campaignId, basicDetails, followUp } = getState().campaignBuilder;
            const payload = toGeneratePostsGuestPayload({
                campaignId,
                basicDetails,
                followUp,
                guestId,
            });
            const data = await generatePostsGuest(payload);
            return normalizeGeneratePostResponse(data);
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;
            return rejectWithValue({
                status,
                ...(typeof data === 'object' && data !== null ? data : { detail: data || error.message }),
            });
        }
    }
);

export const fetchPosts = createAsyncThunk(
    'campaignBuilder/fetchPosts',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { campaignId, basicDetails, followUp, selectedIdeas } =
                getState().campaignBuilder;

            if (!campaignId) {
                return rejectWithValue('Campaign ID is missing');
            }

            const payload = toGeneratePostPayload({
                campaignId,
                basicDetails,
                followUp,
                selectedIdeas,
            });
            const data = await generatePost(payload);
            return normalizeGeneratePostResponse(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const publishCampaign = createAsyncThunk(
    'campaignBuilder/publishCampaign',
    async (_, { getState, dispatch, rejectWithValue }) => {
        try {
            const { campaignId, basicDetails, followUp, selectedIdeas, posts } =
                getState().campaignBuilder;

            if (!campaignId) {
                return rejectWithValue('Campaign ID is missing');
            }

            const payload = toPublishCampaignPayload({
                basicDetails,
                followUp,
                selectedIdeas,
                posts,
            });
            const data = await publishCampaignApi(campaignId, payload);
            await dispatch(fetchCurrentPlan()).unwrap();
            return normalizePublishCampaignResponse(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

/** @deprecated use publishCampaign */
export const createCampaign = publishCampaign;

const initialWizardState = {
    campaignId: null,
    basicDetails: {},
    followUp: {},
    ideas: [],
    selectedIdeas: [],
    posts: [],
    preferredAssetType: null,
};

const campaignBuilderSlice = createSlice({
    name: 'campaignBuilder',
    initialState: {
        loading: false,
        campaignId: null,
        basicDetails: {},
        followUp: {},
        ideas: [],
        selectedIdeas: [],
        posts: [],
        preferredAssetType: null,
        ideasLoading: false,
        postsLoading: false,
        publishLoading: false,
        campaignLoading: false,
        campaignStatus: null,
        ideasError: null,
        postsError: null,
        publishError: null,
        campaignLoadError: null,
        publishedCampaign: null,
        /** @deprecated use publishedCampaign */
        createdCampaign: null,
        error: null,
    },
    reducers: {
        basicDetails: (state, action) => {
            state.basicDetails = action.payload;
        },
        followUp: (state, action) => {
            state.followUp = WIZARD_HASHTAGS_ENABLED
                ? action.payload
                : { ...action.payload, hashtags: [] };
        },
        setPreferredAssetType: (state, action) => {
            state.preferredAssetType = action.payload ?? null;
        },
        setCampaignId: (state, action) => {
            state.campaignId = action.payload;
        },
        toggleIdeaSelection: (state, action) => {
            const idea = action.payload;
            const index = state.selectedIdeas.findIndex((i) => i.id === idea.id);
            if (index >= 0) {
                state.selectedIdeas.splice(index, 1);
            } else {
                state.selectedIdeas.push({ ...idea });
            }
        },
        setSelectedIdeaAssetType: (state, action) => {
            const { ideaId, assetType } = action.payload;
            const idea = state.selectedIdeas.find((i) => i.id === ideaId);
            if (idea) {
                idea.asset_type = assetType;
            }
        },
        updatePost: (state, action) => {
            const { index, field, value } = action.payload;
            if (state.posts[index]) {
                state.posts[index][field] = value;
            }
        },
        updatePostHashtag: (state, action) => {
            const { postIndex, hashtagIndex, value } = action.payload;
            if (state.posts[postIndex]?.hash_tags) {
                state.posts[postIndex].hash_tags[hashtagIndex] = value;
            }
        },
        resetCampaignBuilder: (state) => {
            Object.assign(state, {
                ...initialWizardState,
                ideasLoading: false,
                postsLoading: false,
                publishLoading: false,
                campaignLoading: false,
                ideasError: null,
                postsError: null,
                publishError: null,
                campaignLoadError: null,
                campaignStatus: null,
                publishedCampaign: null,
                createdCampaign: null,
            });
        },
        seedCampaignBuilderFromPreferences: (state, action) => {
            const defaults = getCampaignBuilderDefaults(action.payload?.userId);
            const fastForward = Boolean(action.payload?.fastForward);
            const preferredAssetType = action.payload?.preferredAssetType ?? null;
            const currentBasic = state.basicDetails ?? {};
            const currentFollowUp = state.followUp ?? {};
            const existingName =
                typeof currentBasic.name === "string" ? currentBasic.name.trim() : "";

            state.basicDetails = {
                name:
                    existingName ||
                    (fastForward ? formatQuickCampaignName() : defaults.basicDetails.name),
                platform: currentBasic.platform || defaults.basicDetails.platform,
                language: currentBasic.language || defaults.basicDetails.language,
            };
            const currentOpportunities = getFollowUpOpportunities(currentFollowUp);

            state.followUp = {
                tone: currentFollowUp.tone ?? defaults.followUp.tone,
                topic: currentFollowUp.topic ?? defaults.followUp.topic,
                target: currentFollowUp.target || defaults.followUp.target,
                hashtags: currentFollowUp.hashtags ?? defaults.followUp.hashtags,
                opportunities: currentOpportunities.length
                    ? currentOpportunities
                    : defaults.followUp.opportunities,
            };
            if (preferredAssetType) {
                state.preferredAssetType = preferredAssetType;
            }
        },
        startCampaignFromOpportunity: (state, action) => {
            const { opportunity, opportunities, userId } = action.payload;
            const defaults = getCampaignBuilderDefaults(userId);
            const list = Array.isArray(opportunities) && opportunities.length > 0
                ? opportunities
                : opportunity
                  ? [opportunity]
                  : [];

            Object.assign(state, {
                ...initialWizardState,
                ideasLoading: false,
                postsLoading: false,
                publishLoading: false,
                campaignLoading: false,
                ideasError: null,
                postsError: null,
                publishError: null,
                campaignLoadError: null,
                campaignStatus: null,
                publishedCampaign: null,
                createdCampaign: null,
            });

            state.basicDetails = {
                name: buildOpportunityCampaignNameFromList(list),
                platform: defaults.basicDetails.platform,
                language: defaults.basicDetails.language,
            };
            state.followUp = {
                ...defaults.followUp,
                opportunities: list,
            };
        },
        seedQuickPostCampaign: (state, action) => {
            const { tone, topic, target, hashtags = [], userId } = action.payload;
            const defaults = getCampaignBuilderDefaults(userId);
            const quickDefaults = getQuickCampaignDefaults(userId);

            Object.assign(state, {
                ...initialWizardState,
                ideasLoading: false,
                postsLoading: false,
                publishLoading: false,
                campaignLoading: false,
                ideasError: null,
                postsError: null,
                publishError: null,
                campaignLoadError: null,
                campaignStatus: null,
                publishedCampaign: null,
                createdCampaign: null,
            });

            state.basicDetails = {
                name: quickDefaults.name,
                platform: quickDefaults.platform,
                language: quickDefaults.language,
            };
            state.followUp = {
                ...defaults.followUp,
                tone: tone?.trim() ?? "",
                topic: topic?.trim() ?? "",
                target: target?.trim() || defaults.followUp.target,
                hashtags,
                opportunities: [],
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(initiateCampaign.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(initiateCampaign.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(initiateCampaign.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Something went wrong';
            })
            .addCase(loadCampaign.pending, (state) => {
                state.campaignLoading = true;
                state.campaignLoadError = null;
            })
            .addCase(loadCampaign.fulfilled, (state, action) => {
                state.campaignLoading = false;
                state.campaignId = action.payload.campaignId;
                state.campaignStatus = action.payload.status ?? null;
                state.basicDetails = action.payload.basicDetails;
                state.followUp = action.payload.followUp;
                state.ideas = action.payload.ideas;
                state.selectedIdeas = action.payload.selectedIdeas;
                state.posts = action.payload.posts;
            })
            .addCase(loadCampaign.rejected, (state, action) => {
                state.campaignLoading = false;
                state.campaignLoadError = action.payload || 'Failed to load campaign';
            })
            .addCase(startQuickCampaign.pending, (state) => {
                Object.assign(state, {
                    ...initialWizardState,
                    ideasLoading: true,
                    ideasError: null,
                    postsLoading: false,
                    publishLoading: false,
                    postsError: null,
                    publishError: null,
                    publishedCampaign: null,
                    createdCampaign: null,
                });
            })
            .addCase(startQuickCampaign.fulfilled, (state, action) => {
                state.campaignId = action.payload.campaignId;
                state.basicDetails = action.payload.basicDetails;
                state.followUp = action.payload.followUp;
                state.ideas = action.payload.ideas;
                state.selectedIdeas = [];
                state.ideasLoading = false;
            })
            .addCase(startQuickCampaign.rejected, (state, action) => {
                state.ideasLoading = false;
                state.ideasError = action.payload || 'Failed to start quick campaign';
            })
            .addCase(fetchIdeasCombined.pending, (state) => {
                state.ideasLoading = true;
                state.ideasError = null;
            })
            .addCase(fetchIdeasCombined.fulfilled, (state, action) => {
                state.ideasLoading = false;
                state.campaignId = action.payload.campaignId;
                state.ideas = action.payload.ideas;
                state.selectedIdeas = [];
            })
            .addCase(fetchIdeasCombined.rejected, (state, action) => {
                state.ideasLoading = false;
                state.ideasError = action.payload || 'Failed to generate ideas';
            })
            .addCase(fetchPostsCombined.pending, (state) => {
                state.postsLoading = true;
                state.postsError = null;
            })
            .addCase(fetchPostsCombined.fulfilled, (state, action) => {
                state.postsLoading = false;
                state.campaignId = action.payload.campaignId ?? state.campaignId;
                state.campaignStatus = action.payload.status ?? state.campaignStatus ?? "draft";
                state.posts = action.payload.posts;
            })
            .addCase(fetchPostsCombined.rejected, (state, action) => {
                state.postsLoading = false;
                state.postsError = action.payload || 'Failed to generate posts';
            })
            .addCase(fetchPostsGuest.pending, (state) => {
                state.postsLoading = true;
                state.postsError = null;
            })
            .addCase(fetchPostsGuest.fulfilled, (state, action) => {
                state.postsLoading = false;
                state.campaignId = action.payload.campaignId ?? state.campaignId;
                state.campaignStatus = action.payload.status ?? state.campaignStatus ?? "draft";
                state.posts = action.payload.posts;
            })
            .addCase(fetchPostsGuest.rejected, (state, action) => {
                state.postsLoading = false;
                state.postsError = action.payload || 'Failed to generate posts';
            })
            .addCase(fetchPosts.pending, (state) => {
                state.postsLoading = true;
                state.postsError = null;
            })
            .addCase(fetchPosts.fulfilled, (state, action) => {
                state.postsLoading = false;
                state.campaignId = action.payload.campaignId ?? state.campaignId;
                state.campaignStatus = action.payload.status ?? state.campaignStatus ?? "draft";
                state.posts = action.payload.posts;
            })
            .addCase(fetchPosts.rejected, (state, action) => {
                state.postsLoading = false;
                state.postsError = action.payload || 'Failed to generate posts';
            })
            .addCase(publishCampaign.pending, (state) => {
                state.publishLoading = true;
                state.publishError = null;
            })
            .addCase(publishCampaign.fulfilled, (state, action) => {
                state.publishLoading = false;
                state.publishedCampaign = action.payload;
                state.createdCampaign = action.payload;
            })
            .addCase(publishCampaign.rejected, (state, action) => {
                state.publishLoading = false;
                state.publishError = action.payload || 'Failed to publish campaign';
            });
    },
});

export const {
    basicDetails,
    followUp,
    setCampaignId,
    toggleIdeaSelection,
    setSelectedIdeaAssetType,
    setPreferredAssetType,
    updatePost,
    updatePostHashtag,
    resetCampaignBuilder,
    seedCampaignBuilderFromPreferences,
    startCampaignFromOpportunity,
    seedQuickPostCampaign,
} = campaignBuilderSlice.actions;

export default campaignBuilderSlice.reducer;
